<template>
  <div class="estimate-document-editor">
    <div v-if="errorMessages.length" class="alert alert-danger" role="alert">
      <div v-for="message in errorMessages" :key="message">{{ message }}</div>
    </div>
    <div :class="['editor-toolbar', { 'editor-toolbar--portrait': !isLandscape }]">
      <div class="editor-title">
        <strong>{{ model.id ? '見積書 編集' : '見積書 新規作成' }}</strong>
        <span>― {{ editorLayoutLabel }}</span>
      </div>
      <div class="toolbar-group toolbar-group--right">
        <button
          class="btn btn-sm btn-outline-light"
          type="button"
          :disabled="isSubmitting || isGeneratingPreview"
          @click="emit('preview')"
        >
          {{ isGeneratingPreview ? 'プレビュー作成中...' : 'PDFプレビュー' }}
        </button>
        <button class="btn btn-sm btn-outline-light" type="button" :disabled="isSubmitting" @click="emit('cancel')">
          戻る
        </button>
        <button class="btn btn-sm btn-primary" type="submit" :disabled="isSubmitting">
          {{ isSubmitting ? '保存中...' : submitLabel }}
        </button>
      </div>
    </div>

    <div class="editor-options">
      <div class="toolbar-group toolbar-group--settings">
        <div class="toolbar-control">
          <span class="toolbar-label">帳票形式</span>
          <template v-if="showLayoutSelector">
            <button
              v-for="option in layoutOptions"
              :key="option.value"
              :class="['btn btn-sm', isLayoutSelected(option.value) ? 'btn-primary' : 'btn-outline-primary']"
              type="button"
              :aria-pressed="isLayoutSelected(option.value)"
              @click="selectLayout(option.value)"
            >
              {{ option.label }}
            </button>
          </template>
          <span v-else class="badge text-bg-primary">{{ currentLayoutLabel }}</span>
        </div>
        <div class="toolbar-control" role="group" aria-label="見積書の言語">
          <span class="toolbar-label">見積書言語</span>
          <button
            v-for="option in languageOptions"
            :key="option.value"
            :class="['btn btn-sm', model.default_language_type === option.value ? 'btn-primary' : 'btn-outline-primary']"
            type="button"
            :aria-pressed="model.default_language_type === option.value"
            @click="model.default_language_type = option.value"
          >
            {{ option.label }}
          </button>
        </div>
        <div v-if="!isLandscape" class="toolbar-control">
          <span class="toolbar-label">見積種別</span>
          <button
            v-for="option in estimateTypeOptions"
            :key="option.value"
            :class="['btn btn-sm', model.estimate_type === option.value ? 'btn-primary' : 'btn-outline-primary']"
            type="button"
            :aria-pressed="model.estimate_type === option.value"
            @click="selectEstimateType(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
        <div class="toolbar-control">
          <span class="toolbar-label">会社印</span>
          <button
            :class="['btn btn-sm', model.seal_flg ? 'btn-primary' : 'btn-outline-primary']"
            type="button"
            :aria-pressed="Boolean(model.seal_flg)"
            @click="model.seal_flg = !model.seal_flg"
          >
            押印 {{ model.seal_flg ? 'あり' : 'なし' }}
          </button>
        </div>
      </div>
    </div>

    <button
      class="management-toggle-bar"
      type="button"
      :aria-expanded="managementOpen"
      aria-controls="estimate-management-panel"
      @click="managementOpen = !managementOpen"
    >
      <span class="management-toggle-caret">{{ managementOpen ? '▼' : '▶' }}</span>
      <span class="management-toggle-title">管理情報</span>
      <span class="management-toggle-summary">
        {{ isLandscape
          ? '税設定・社内メモ・工事項目設定'
          : '有効期限・税設定・社内メモ・明細設定' }}
      </span>
      <span class="management-toggle-help">{{ managementOpen ? 'クリックして閉じる' : 'クリックして開く' }}</span>
    </button>

    <section
      v-if="managementOpen"
      id="estimate-management-panel"
      class="management-panel"
      :aria-label="isLandscape ? '横型見積の管理項目' : 'PDF非出力の管理項目'"
    >
      <div class="management-heading">
        <div>
          <span class="management-kicker">{{ isLandscape ? '見積管理' : '帳票外管理' }}</span>
          <h2>{{ isLandscape ? '管理情報' : '管理項目' }}</h2>
        </div>
        <p>{{ isLandscape ? '税設定・社内メモ・工事項目を管理します。' : '帳票に出力しない管理項目です。' }}</p>
      </div>
      <div class="row g-3">
        <div v-if="!isLandscape" class="col-md-3">
          <label class="form-label">有効期限</label>
          <input v-model="model.valid_until" class="form-control" type="date">
        </div>
        <div class="col-md-1">
          <label class="form-label">
            税率(%)
            <span class="badge text-bg-danger">必須</span>
          </label>
          <input v-model.number="model.tax_rate" class="form-control" type="number" min="0" max="100" step="0.01" required>
        </div>
        <div class="col-md-2">
          <label class="form-label">税端数処理</label>
          <select v-model="model.tax_rounding_type" class="form-select">
            <option value="floor">切捨て</option>
            <option value="round">四捨五入</option>
            <option value="ceil">切上げ</option>
          </select>
        </div>
        <div class="col-md-6">
          <label class="form-label">社内備考</label>
          <textarea v-model="model.internal_notes" class="form-control" rows="3"></textarea>
        </div>
      </div>

      <details v-if="isLandscape && visibleSections.length" class="section-management mt-4">
        <summary>工事項目の管理設定</summary>
        <div class="table-responsive mt-3">
          <table class="table table-sm align-middle">
            <thead><tr><th>工事項目</th><th>業務区分</th></tr></thead>
            <tbody>
              <tr v-for="section in visibleSections" :key="`management-${section.local_key}`">
                <td>{{ section.section_nm || '名称未入力' }}</td>
                <td>
                  <select
                    v-model="section.m_business_classification_id"
                    class="form-select form-select-sm"
                    @change="emit('classification-changed', section)"
                  >
                    <option value="">未選択</option>
                    <option
                      v-for="classification in classifications"
                      :key="classification.id"
                      :value="classification.id"
                    >{{ classification.business_classification_nm }}</option>
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>

      <details v-if="!isLandscape && portraitManagementLines.length" class="section-management mt-4">
        <summary>明細の管理設定</summary>
        <div class="table-responsive mt-3">
          <table class="table table-sm align-middle">
            <thead><tr><th>品名・作業名</th><th>業務区分</th></tr></thead>
            <tbody>
              <tr v-for="line in portraitManagementLines" :key="`management-line-${line.local_key || line.id}`">
                <td>{{ line.item_nm || '名称未入力' }}</td>
                <td>
                  <select
                    v-model="line.m_business_classification_id"
                    class="form-select form-select-sm"
                    @change="emit('classification-changed', line)"
                  >
                    <option value="">未選択</option>
                    <option
                      v-for="classification in classifications"
                      :key="classification.id"
                      :value="classification.id"
                    >{{ classification.business_classification_nm }}</option>
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>
    </section>

    <div class="document-stage">
      <template v-if="!isLandscape">
        <section
          v-for="(page, pageIndex) in portraitPages"
          :key="`portrait-editor-page-${pageIndex}`"
          class="document-paper document-paper--portrait"
        >
          <EstimateDocumentHeader
            v-if="pageIndex === 0"
            v-model="model"
            orientation="portrait"
            :variant="portraitVariant"
            :customers="customers"
            :representatives="filteredRepresentatives"
            :buildings="filteredBuildings"
            :preview="preview"
            :page-label="portraitPages.length > 1 ? (isEnglish ? `(Page ${pageIndex + 1})` : `（${pageIndex + 1}枚目）`) : ''"
            @customer-changed="emit('customer-changed')"
          />
          <div v-else class="continuation-header">
            <div>{{ recipientDisplay }}</div>
            <h2>{{ isEnglish ? 'QUOTATION' : '御 見 積 書' }} <small>{{ isEnglish ? `(Page ${pageIndex + 1})` : `（${pageIndex + 1}枚目）` }}</small></h2>
            <div>No. {{ model.estimate_no || (isEnglish ? 'Not assigned' : '未採番') }}<br>{{ model.issue_date }}</div>
          </div>

          <table class="document-grid portrait-grid">
            <thead>
              <tr><th>{{ isEnglish ? 'Description' : '項目' }} <span class="badge text-bg-danger" :title="isEnglish ? 'Required for detail lines' : '通常明細では必須'">{{ isEnglish ? 'Required' : '必須' }}</span></th><th>{{ isEnglish ? 'Qty.' : '数量' }}</th><th>{{ isEnglish ? 'Unit' : '単位' }}</th><th>{{ isEnglish ? 'Unit Price' : '単価' }}</th><th>{{ isEnglish ? 'Amount' : '金額' }}</th><th>{{ isEnglish ? 'Notes' : '摘要' }}</th></tr>
            </thead>
            <tbody>
              <template v-for="row in page" :key="row.key">
                <!-- 注記はPDFと同じく「項目」列の中だけに入力する -->
                <tr v-if="row.line.line_type === 'note'" class="note-row">
                  <td>
                    <textarea v-model="displayedLine(row.line).item" class="cell-input" :rows="textRows(displayedLine(row.line).item, 32)" :placeholder="isEnglish ? '* Enter a note (line breaks allowed)' : '※注意書きを入力（改行可）'"></textarea>
                  </td>
                  <td></td><td></td><td></td><td></td>
                  <td class="row-action-anchor">
                    <button class="remove-record-button remove-record-button--outside" type="button" title="この行を削除" aria-label="この行を削除" @click="emitRemoveLine(rowLines(row), row.line)">×</button>
                  </td>
                </tr>
                <tr v-else-if="row.line.line_type === 'subtotal'" class="subtotal-row">
                  <td colspan="4">{{ isEnglish ? 'Subtotal' : '小計' }}</td>
                  <td colspan="2" class="number-cell row-action-anchor">
                    {{ formatNumber(sectionNet(row.section)) }}
                    <button class="remove-record-button remove-record-button--outside" type="button" title="この行を削除" aria-label="この行を削除" @click="emitRemoveLine(rowLines(row), row.line)">×</button>
                  </td>
                </tr>
                <template v-else>
                  <tr class="estimate-detail-main-row">
                    <td>
                      <EstimateItemNameSearchInput
                        v-if="row.line.line_type === 'detail'"
                        v-model="row.line.item_nm"
                        :suggestions="itemNameSuggestions"
                        :portrait="!isLandscape"
                        required
                        :placeholder="isEnglish ? 'Description' : '品名・作業名'"
                        @search="emit('search-item-name-suggestions', $event)"
                      />
                      <input v-else v-model.trim="displayedLine(row.line).item" class="cell-input" type="text" :placeholder="isEnglish ? (row.line.line_type === 'discount' ? 'Discount' : 'Description') : (row.line.line_type === 'discount' ? '値引名' : '品名・作業名')">
                    </td>
                    <!-- 値引は金額だけを入力する（数量・単位・単価は使わない） -->
                    <template v-if="row.line.line_type === 'discount'"><td></td><td></td><td></td></template>
                    <template v-else>
                      <td><input v-model="row.line.quantity" class="cell-input number-input" type="number" min="0" step="0.01" @input="captureQuantityDecimalPlaces(row.line, $event.target.value)"></td>
                      <td><input v-model.trim="displayedLine(row.line).unit" class="cell-input" type="text"></td>
                      <td><input v-model="row.line.unit_price" class="cell-input number-input" type="number" min="0" step="0.01"></td>
                    </template>
                    <td class="number-cell" :class="{ 'manual-amount': row.line.manual_amount_flg }">
                      <div v-if="row.line.line_type === 'discount'" class="discount-amount-input"><span>-</span><input v-model="row.line.amount" class="cell-input number-input" type="number" min="0" step="1" :placeholder="isEnglish ? 'Amount' : '値引額'"></div>
                      <template v-else>{{ formatNumber(lineAmount(row.line)) }}</template>
                    </td>
                    <td class="portrait-remarks-cell row-action-anchor">
                      <textarea v-model="displayedLine(row.line).specification" class="cell-input" :rows="textRows(displayedLine(row.line).specification, 26)" :placeholder="isEnglish ? 'Notes' : '仕様・部位・補足など'"></textarea>
                      <button class="remove-record-button remove-record-button--outside" type="button" title="この行を削除" aria-label="この行を削除" @click="emitRemoveLine(rowLines(row), row.line)">×</button>
                    </td>
                  </tr>
                </template>
              </template>
              <tr v-if="pageIndex === portraitPages.length - 1 && portraitVariant === 'annual'" class="fixed-note-row">
                <td>{{ isEnglish ? '* Consumption tax is not included in this quotation.' : '※本見積に消費税は含んでおりません' }}</td><td></td><td></td><td></td><td></td><td></td>
              </tr>
              <template v-if="pageIndex === portraitPages.length - 1">
                <template v-if="portraitVariant === 'annual'">
                  <tr class="subtotal-row">
                    <td colspan="4">{{ isEnglish ? 'Subtotal' : '小計' }}</td><td colspan="2" class="number-cell">{{ formatNumber(preview.net) }}</td>
                  </tr>
                  <tr class="total-row">
                    <td colspan="4">{{ isEnglish ? 'Total' : '合計（年額・税抜）' }}</td><td colspan="2" class="number-cell">{{ formatNumber(preview.net) }}</td>
                  </tr>
                </template>
                <template v-else-if="isJapaneseTemporary">
                  <tr class="subtotal-row">
                    <td colspan="4">小計</td><td colspan="2" class="number-cell">{{ formatNumber(preview.subtotal) }}</td>
                  </tr>
                  <tr v-if="hasDiscount" class="subtotal-row">
                    <td colspan="4">お値引き</td><td colspan="2" class="number-cell">-{{ formatNumber(preview.discount) }}</td>
                  </tr>
                  <tr class="subtotal-row">
                    <td colspan="4">税抜金額</td><td colspan="2" class="number-cell">{{ formatNumber(preview.net) }}</td>
                  </tr>
                  <tr class="subtotal-row">
                    <td colspan="4">{{ formatEstimateTaxLabel(model.tax_rate) }}</td><td colspan="2" class="number-cell">{{ formatNumber(preview.tax) }}</td>
                  </tr>
                  <tr class="total-row">
                    <td colspan="4">合計（税込）</td><td colspan="2" class="number-cell">{{ formatNumber(preview.total) }}</td>
                  </tr>
                </template>
                <tr v-else class="total-row">
                  <td colspan="4">{{ isEnglish ? 'Total' : '合計' }}</td><td colspan="2" class="number-cell">{{ formatNumber(preview.net) }}</td>
                </tr>
              </template>
              <tr v-else class="total-row"><td colspan="4">{{ isEnglish ? 'Carried Forward' : '次頁へ' }}</td><td colspan="2">{{ isEnglish ? '' : '続く' }}</td></tr>
              <tr v-if="pageIndex === portraitPages.length - 1" class="portrait-line-actions-row">
                <td colspan="6">
                  <div class="document-row-add-actions">
                    <button type="button" @click="emitAddLine(model.unsectioned_lines, 'detail')">＋ 行を追加</button>
                    <button v-if="ESTIMATE_FREQUENT_WORKS_ENABLED" type="button" class="frequent-work-button" @click="emit('select-frequent-work', model.unsectioned_lines)">よく使う作業から選ぶ</button>
                    <button type="button" @click="emitAddLine(model.unsectioned_lines, 'discount')">＋ 値引を追加</button>
                    <button type="button" @click="emitAddLine(model.unsectioned_lines, 'note')">＋ 注記を追加</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <details
            v-if="pageIndex === portraitPages.length - 1"
            class="editor-general-notes"
            :open="Boolean(model.general_notes)"
          >
            <summary>{{ isEnglish ? 'Quotation notes' : '見積書備考（必要な場合のみ入力）' }}</summary>
            <div class="editor-general-notes-body">
              <textarea
                :id="`portrait-general-notes-${pageIndex}`"
                v-model="model.general_notes"
                class="cell-input editor-general-notes-input"
                rows="2"
              ></textarea>
            </div>
          </details>
          <div v-if="pageIndex === portraitPages.length - 1" class="editor-quotation-conditions">
            <div v-for="condition in portraitQuotationConditions" :key="condition">・{{ condition }}</div>
          </div>
          <div v-if="portraitPages.length > 1" class="editor-page-number">{{ pageIndex + 1 }} / {{ portraitPages.length }}</div>
        </section>
      </template>

      <template v-else>
        <section class="document-paper document-paper--landscape cover-editor-page">
          <EstimateDocumentHeader
            v-model="model"
            orientation="landscape"
            variant="landscape"
            :customers="customers"
            :representatives="filteredRepresentatives"
            :buildings="filteredBuildings"
            :preview="preview"
            @customer-changed="emit('customer-changed')"
          />
          <div class="cover-editor-note">
            表紙の合計・工事費・消費税は、内訳明細の同じデータから自動表示しています。
          </div>
        </section>

        <section class="document-paper document-paper--landscape landscape-detail-editor-page">
          <div class="readonly-sync-note readonly-sync-note--top">
            工事別内訳書は入力した内訳明細から自動生成されます。内容はPDFプレビューで確認できます。
          </div>
          <div
            v-for="(section, sectionIndex) in visibleSections"
            :key="`detail-section-${section.local_key || section.id || sectionIndex}`"
            class="landscape-section-block"
          >
              <div class="landscape-section-heading">
              <span class="landscape-section-number">{{ roman(sectionIndex + 1) }}</span>
              <div class="landscape-section-name">
                <div class="required-field-label">
                  <span>{{ isEnglish ? 'Work Item' : '工事項目名' }}</span>
                  <span class="badge text-bg-danger">{{ isEnglish ? 'Required' : '必須' }}</span>
                </div>
                <input v-model.trim="displayedSection(section).name" class="cell-input" type="text" required :placeholder="isEnglish ? 'Work Item' : '工事項目名'">
              </div>
              <div class="landscape-section-heading-actions">
                <span class="landscape-section-subtotal">{{ isEnglish ? 'Subtotal' : '小計' }} <strong>{{ formatNumber(sectionNet(section)) }}</strong>{{ isEnglish ? '' : ' 円' }}</span>
                <button class="remove-record-button" type="button" @click="emit('remove-section', section)">工事項目を削除</button>
              </div>
            </div>
            <div class="landscape-section-summary-settings">
              <label>
                <span>{{ isEnglish ? 'Qty.' : '工事別数量' }} <span class="badge text-bg-danger">{{ isEnglish ? 'Required' : '必須' }}</span></span>
                <input v-model="section.quantity" class="cell-input number-input" type="number" min="0" step="0.01" required @input="captureQuantityDecimalPlaces(section, $event.target.value)">
              </label>
              <label>
                <span>{{ isEnglish ? 'Unit' : '工事別単位' }} <span class="badge text-bg-danger">{{ isEnglish ? 'Required' : '必須' }}</span></span>
                <input v-model.trim="displayedSection(section).unit" class="cell-input" type="text" required>
              </label>
              <span>{{ isEnglish ? 'Applied to the work breakdown quantity and unit.' : '「工事別内訳書」の数量・単位に反映' }}</span>
            </div>
            <div class="landscape-section-notes">
              <span>{{ isEnglish ? 'Notes' : '工事項目備考' }}</span>
              <input v-model.trim="displayedSection(section).notes" class="cell-input" type="text" :placeholder="isEnglish ? 'Work item notes' : '工事別内訳書の備考欄に表示する備考'">
            </div>

            <table class="document-grid landscape-detail-grid">
              <thead><tr><th>{{ isEnglish ? 'Description' : '名称' }} <span class="badge text-bg-danger" :title="isEnglish ? 'Required for detail lines' : '通常明細では必須'">{{ isEnglish ? 'Required' : '必須' }}</span></th><th>{{ isEnglish ? 'Part' : '部位' }}</th><th>{{ isEnglish ? 'Specification' : '仕様' }}</th><th>{{ isEnglish ? 'Qty.' : '数量' }}</th><th>{{ isEnglish ? 'Unit' : '単位' }}</th><th>{{ isEnglish ? 'Unit Price' : '単価' }}</th><th>{{ isEnglish ? 'Amount' : '金額' }}</th></tr></thead>
              <tbody>
                <template v-for="(line, lineIndex) in visibleRecords(section.lines)" :key="line.local_key || line.id || lineIndex">
                  <tr v-if="line.line_type === 'note'" class="note-row">
                    <td colspan="7" class="row-action-anchor">
                      <textarea v-model="displayedLine(line).item" class="cell-input" rows="3" :placeholder="isEnglish ? 'Enter a note' : '注記を入力'"></textarea>
                      <button class="remove-record-button remove-record-button--outside" type="button" title="この行を削除" aria-label="この行を削除" @click="emitRemoveLine(section.lines, line)">×</button>
                    </td>
                  </tr>
                  <tr v-else-if="line.line_type === 'subtotal'" class="subtotal-row">
                    <td colspan="6">{{ isEnglish ? 'Subtotal' : '小計' }}</td><td class="number-cell row-action-anchor">{{ formatNumber(sectionNet(section)) }}<button class="remove-record-button remove-record-button--outside" type="button" title="この行を削除" aria-label="この行を削除" @click="emitRemoveLine(section.lines, line)">×</button></td>
                  </tr>
                  <template v-else>
                    <tr class="estimate-detail-main-row">
                      <td>
                        <EstimateItemNameSearchInput
                          v-if="line.line_type === 'detail'"
                          v-model="line.item_nm"
                          :suggestions="itemNameSuggestions"
                          required
                          :placeholder="isEnglish ? 'Description' : '名称'"
                          @search="emit('search-item-name-suggestions', $event)"
                        />
                        <input v-else v-model.trim="displayedLine(line).item" class="cell-input" type="text" :placeholder="isEnglish ? (line.line_type === 'discount' ? 'Discount' : 'Description') : (line.line_type === 'discount' ? '値引名' : '名称')">
                      </td>
                      <td><input v-model.trim="displayedLine(line).part" class="cell-input" type="text"></td>
                      <td><textarea v-model="displayedLine(line).specification" class="cell-input" rows="1"></textarea></td>
                      <template v-if="line.line_type === 'discount'"><td></td><td></td><td></td></template>
                      <template v-else>
                        <td><input v-model="line.quantity" class="cell-input number-input" type="number" min="0" step="0.01" @input="captureQuantityDecimalPlaces(line, $event.target.value)"></td>
                        <td><input v-model.trim="displayedLine(line).unit" class="cell-input" type="text"></td>
                        <td><input v-model="line.unit_price" class="cell-input number-input" type="number" min="0" step="0.01"></td>
                      </template>
                      <td class="number-cell row-action-anchor" :class="{ 'manual-amount': line.manual_amount_flg }"><div v-if="line.line_type === 'discount'" class="discount-amount-input"><span>-</span><input v-model="line.amount" class="cell-input number-input" type="number" min="0" step="1" :placeholder="isEnglish ? 'Amount' : '値引額'"></div><template v-else>{{ formatNumber(lineAmount(line)) }}</template><button class="remove-record-button remove-record-button--outside" type="button" title="この行を削除" aria-label="この行を削除" @click="emitRemoveLine(section.lines, line)">×</button></td>
                    </tr>
                    <tr class="detail-remarks-row"><td colspan="7"><div class="detail-remarks-editor"><span class="detail-remarks-label">{{ isEnglish ? 'Notes' : '備考' }}</span><textarea v-model="displayedLine(line).notes" class="cell-input" rows="3"></textarea></div></td></tr>
                  </template>
                </template>
              </tbody>
            </table>

            <div class="landscape-section-actions">
              <button type="button" @click="emitAddLine(section.lines, 'detail')">＋ 明細を追加</button>
              <button v-if="ESTIMATE_FREQUENT_WORKS_ENABLED" type="button" class="frequent-work-button" @click="emit('select-frequent-work', section.lines)">よく使う作業から選ぶ</button>
              <button type="button" @click="emitAddLine(section.lines, 'discount')">＋ 値引を追加</button>
              <button type="button" @click="emitAddLine(section.lines, 'note')">＋ 注記を追加</button>
            </div>
          </div>

          <div v-if="visibleRecords(model.unsectioned_lines).length" class="landscape-section-block">
            <div class="landscape-section-heading landscape-section-heading--adjustment">
              <span class="landscape-section-number">{{ isEnglish ? 'Adjustment' : '調整' }}</span>
              <strong>{{ isEnglish ? 'General Notes / Discount' : '全体注記・全体値引' }}</strong>
            </div>
            <table class="document-grid landscape-detail-grid">
              <thead><tr><th>{{ isEnglish ? 'Description' : '名称' }}</th><th>{{ isEnglish ? 'Part' : '部位' }}</th><th>{{ isEnglish ? 'Specification' : '仕様' }}</th><th>{{ isEnglish ? 'Qty.' : '数量' }}</th><th>{{ isEnglish ? 'Unit' : '単位' }}</th><th>{{ isEnglish ? 'Unit Price' : '単価' }}</th><th>{{ isEnglish ? 'Amount' : '金額' }}</th></tr></thead>
              <tbody>
                <template v-for="(line, lineIndex) in visibleRecords(model.unsectioned_lines)" :key="line.local_key || line.id || lineIndex">
                  <tr v-if="line.line_type === 'note'" class="note-row">
                    <td colspan="7" class="row-action-anchor">
                      <textarea v-model="displayedLine(line).item" class="cell-input" rows="3" :placeholder="isEnglish ? 'Enter a note' : '注記を入力'"></textarea>
                      <button class="remove-record-button remove-record-button--outside" type="button" title="この行を削除" aria-label="この行を削除" @click="emitRemoveLine(model.unsectioned_lines, line)">×</button>
                    </td>
                  </tr>
                  <template v-else>
                    <tr class="estimate-detail-main-row">
                      <td>
                        <EstimateItemNameSearchInput
                          v-if="line.line_type === 'detail'"
                          v-model="line.item_nm"
                          :suggestions="itemNameSuggestions"
                          :placeholder="isEnglish ? 'Description' : '名称'"
                          @search="emit('search-item-name-suggestions', $event)"
                        />
                        <input v-else v-model.trim="displayedLine(line).item" class="cell-input" type="text" :placeholder="isEnglish ? (line.line_type === 'discount' ? 'Discount' : 'Description') : (line.line_type === 'discount' ? '値引名' : '名称')">
                      </td>
                      <td><input v-model.trim="displayedLine(line).part" class="cell-input" type="text"></td>
                      <td><textarea v-model="displayedLine(line).specification" class="cell-input" rows="1"></textarea></td>
                      <template v-if="line.line_type === 'discount'"><td></td><td></td><td></td></template>
                      <template v-else>
                      <td><input v-model="line.quantity" class="cell-input number-input" type="number" min="0" step="0.01" @input="captureQuantityDecimalPlaces(line, $event.target.value)"></td>
                        <td><input v-model.trim="displayedLine(line).unit" class="cell-input" type="text"></td>
                        <td><input v-model="line.unit_price" class="cell-input number-input" type="number" min="0" step="0.01"></td>
                      </template>
                      <td class="number-cell row-action-anchor" :class="{ 'manual-amount': line.manual_amount_flg }"><div v-if="line.line_type === 'discount'" class="discount-amount-input"><span>-</span><input v-model="line.amount" class="cell-input number-input" type="number" min="0" step="1" :placeholder="isEnglish ? 'Amount' : '値引額'"></div><template v-else>{{ formatNumber(lineAmount(line)) }}</template><button class="remove-record-button remove-record-button--outside" type="button" title="この行を削除" aria-label="この行を削除" @click="emitRemoveLine(model.unsectioned_lines, line)">×</button></td>
                    </tr>
                    <tr class="detail-remarks-row"><td colspan="7"><div class="detail-remarks-editor"><span class="detail-remarks-label">{{ isEnglish ? 'Notes' : '備考' }}</span><textarea v-model="displayedLine(line).notes" class="cell-input" rows="3"></textarea></div></td></tr>
                  </template>
                </template>
              </tbody>
            </table>
          </div>

          <div class="landscape-detail-footer-actions">
            <button type="button" @click="emit('add-section')">＋ 工事項目を追加</button>
            <button type="button" @click="emitAddLine(model.unsectioned_lines, 'note')">＋ 全体注記を追加</button>
            <button type="button" @click="emitAddLine(model.unsectioned_lines, 'discount')">＋ 全体値引を追加</button>
          </div>
        </section>
      </template>
    </div>

    <div v-if="!isLandscape" class="editor-footer-actions">
      <div class="amount-summary">
        <span>明細小計 {{ formatCurrency(preview.subtotal) }}</span>
        <span>値引 -{{ formatCurrency(preview.discount) }}</span>
        <span>消費税 {{ formatCurrency(preview.tax) }}</span>
        <strong>税込合計 {{ formatCurrency(preview.total) }}</strong>
      </div>
    </div>

  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { formatCurrency } from '@/utils/estimates';
import {
  formatEnglishGregorianDate,
  formatJapaneseGregorianDate
} from '@/utils/estimatePdfDates';
import {
  PORTRAIT_ROWS_PER_PAGE,
  buildPortraitRows,
  calculateLineAmount,
  calculateSectionAmounts,
  formatEstimateTaxLabel,
  isEnglishEstimateLanguage,
  mergeEstimatePortraitRemarks,
  paginateRowsByWeight,
  visibleRecords,
  wrappedTextLineCount
} from '@/utils/estimateEditor';
import { ESTIMATE_FREQUENT_WORKS_ENABLED } from '@/utils/estimateFrequentWorks';
import EstimateDocumentHeader from './EstimateDocumentHeader.vue';
import EstimateItemNameSearchInput from './EstimateItemNameSearchInput.vue';

const props = defineProps({
  modelValue: { type: Object, required: true },
  customers: { type: Array, default: () => [] },
  representatives: { type: Array, default: () => [] },
  buildings: { type: Array, default: () => [] },
  classifications: { type: Array, default: () => [] },
  itemNameSuggestions: { type: Array, default: () => [] },
  preview: { type: Object, required: true },
  errorMessages: { type: Array, default: () => [] },
  isSubmitting: { type: Boolean, default: false },
  isGeneratingPreview: { type: Boolean, default: false },
  submitLabel: { type: String, default: '保存' },
  showLayoutSelector: { type: Boolean, default: true }
});

const emit = defineEmits([
  'update:modelValue',
  'preview',
  'cancel',
  'customer-changed',
  'classification-changed',
  'add-section',
  'add-line',
  'remove-section',
  'remove-line',
  'select-frequent-work',
  'search-item-name-suggestions'
]);

const model = computed({ get: () => props.modelValue, set: value => emit('update:modelValue', value) });
const managementOpen = ref(false);
const layoutOptions = [
  { value: 'portrait', label: '縦型' },
  { value: 'landscape_detailed', label: '横型・詳細' }
];
const languageOptions = [
  { value: 'japanese', label: '日本語' },
  { value: 'english', label: 'English' }
];
const estimateTypeOptions = [
  { value: 'temporary', label: '臨時' },
  { value: 'annual', label: '年間' }
];
const isPortraitLayout = layoutType => ['portrait_annual', 'portrait_temporary'].includes(layoutType);
const isLayoutSelected = layoutType => (
  layoutType === 'portrait'
    ? isPortraitLayout(model.value.output_layout_type)
    : model.value.output_layout_type === layoutType
);
const currentLayoutLabel = computed(() =>
  layoutOptions.find(option => isLayoutSelected(option.value))?.label || ''
);
const isLandscape = computed(() => model.value.output_layout_type === 'landscape_detailed');
const editorLayoutLabel = computed(() => (
  isLandscape.value
    ? '横型・詳細'
    : `縦型・${model.value.estimate_type === 'annual' ? '年間' : '臨時'}`
));
const portraitVariant = computed(() => model.value.output_layout_type === 'portrait_annual' ? 'annual' : 'temporary');
const isEnglish = computed(() => isEnglishEstimateLanguage(model.value.default_language_type));
const isJapaneseTemporary = computed(() => portraitVariant.value === 'temporary' && !isEnglish.value);
const hasDiscount = computed(() => Number(props.preview.discount || 0) > 0);
const selectedCustomer = computed(() =>
  props.customers.find(customer => String(customer.id) === String(model.value.m_customer_id))
);
const selectedRepresentative = computed(() =>
  props.representatives.find(representative =>
    String(representative.id) === String(model.value.m_customer_representative_id)
  )
);
const recipientDisplay = computed(() => {
  const recipient = model.value.recipient || {};
  const customerName = selectedCustomer.value?.customer_nm || recipient.customer_nm || '';
  const departmentName = selectedRepresentative.value?.in_charge_department_nm ||
    recipient.department_nm || '';
  const parts = [customerName, departmentName].filter(Boolean);
  if (!parts.length) return isEnglish.value ? 'Recipient not selected' : '宛先未選択';
  return isEnglish.value ? parts.join(', ') : [...parts, '御中'].join(' ');
});
const portraitQuotationConditions = computed(() => {
  const conditions = [];
  if (model.value.valid_until) {
    const validUntil = isEnglish.value
      ? formatEnglishGregorianDate(model.value.valid_until)
      : formatJapaneseGregorianDate(model.value.valid_until);
    conditions.push(
      isEnglish.value
        ? `This quotation is valid until ${validUntil}.`
        : `本見積の有効期限は${validUntil}までです。`
    );
  }
  return conditions;
});
const visibleSections = computed(() => visibleRecords(model.value.sections));
const portraitManagementLines = computed(() => [
  ...visibleSections.value.flatMap(section => visibleRecords(section.lines)),
  ...visibleRecords(model.value.unsectioned_lines)
].filter(line => line.line_type === 'detail'));
let layoutSectionKeySequence = 0;
const movePortraitLinesIntoLandscapeSection = () => {
  const sourceLines = visibleRecords(model.value.unsectioned_lines);
  if (!sourceLines.length) return;

  const hasExistingSection = visibleSections.value.length > 0;
  model.value.sections.push({
    local_key: `layout-section-${Date.now()}-${layoutSectionKeySequence++}`,
    id: undefined,
    sort_no: model.value.sections.length + 1,
    section_nm: hasExistingSection
      ? (isEnglish.value ? 'Additional Work' : '追加明細')
      : (model.value.title || (isEnglish.value ? 'Work Item 1' : '工事項目1')),
    subcategory_nm: '',
    m_business_classification_id:
      sourceLines.find(line => line.m_business_classification_id)?.m_business_classification_id || '',
    m_estimate_subcategory_id: '',
    notes: '',
    quantity: 1,
    quantity_decimal_places: 0,
    unit_nm: isEnglish.value ? 'Lot' : '式',
    _destroy: false,
    lines: sourceLines
  });
  model.value.unsectioned_lines = (model.value.unsectioned_lines || [])
    .filter(line => line._destroy);
};
// 入力欄の幅（全角文字数）で折り返した行数ぶん高さを取り、長文でも全文が見えるようにする
const textRows = (value, columnsPerLine) => wrappedTextLineCount(value, columnsPerLine);
const selectLayout = layoutType => {
  if (!['annual', 'temporary'].includes(model.value.estimate_type)) {
    model.value.estimate_type = 'temporary';
  }
  if (layoutType === 'landscape_detailed') {
    movePortraitLinesIntoLandscapeSection();
    model.value.estimate_type = 'temporary';
  } else {
    mergeEstimatePortraitRemarks(model.value);
  }
  model.value.output_layout_type = layoutType === 'portrait'
    ? (model.value.estimate_type === 'annual' ? 'portrait_annual' : 'portrait_temporary')
    : layoutType;
};
const selectEstimateType = estimateType => {
  model.value.estimate_type = estimateType;
  if (isPortraitLayout(model.value.output_layout_type)) {
    model.value.output_layout_type = estimateType === 'annual' ? 'portrait_annual' : 'portrait_temporary';
  }
};
const filteredRepresentatives = computed(() => {
  if (!model.value.m_customer_id) return props.representatives;
  return props.representatives.filter(representative =>
    String(representative.m_customer_id || representative.m_customer?.id) === String(model.value.m_customer_id)
  );
});
const filteredBuildings = computed(() => {
  if (!model.value.m_customer_id) return props.buildings;
  return props.buildings.filter(building =>
    String(building.m_customer_id || building.m_customer?.id) === String(model.value.m_customer_id)
  );
});
const portraitFinalReservedRows = computed(() => {
  const conditionRows = portraitQuotationConditions.value.length > 0 ? 2 : 0;
  if (isJapaneseTemporary.value) return (hasDiscount.value ? 7 : 6) + conditionRows;
  return (portraitVariant.value === 'annual' ? 4 : 3) + conditionRows;
});
const isPortraitDetailRow = row =>
  row.kind === 'line' && !['note', 'subtotal'].includes(row.line?.line_type);
const portraitRowWeight = row => {
  if (!row.line) return 1;
  if (row.line.line_type === 'note') {
    return wrappedTextLineCount([
      displayedLine(row.line).item,
      displayedLine(row.line).specification,
      displayedLine(row.line).notes
    ].filter(Boolean).join('\n'), 32);
  }
  if (isPortraitDetailRow(row)) {
    const itemWeight = wrappedTextLineCount(displayedLine(row.line).item, 32);
    const remarksWeight = wrappedTextLineCount([
      displayedLine(row.line).part,
      displayedLine(row.line).specification,
      displayedLine(row.line).notes
    ].filter(Boolean).join('\n'), 26);
    return Math.max(itemWeight, remarksWeight);
  }
  return 1;
};
const portraitPages = computed(() => paginateRowsByWeight(
  buildPortraitRows(model.value),
  PORTRAIT_ROWS_PER_PAGE,
  portraitFinalReservedRows.value,
  portraitRowWeight
));
const lineAmount = line => calculateLineAmount(line);
const sectionNet = section => section ? calculateSectionAmounts(section).net : props.preview.net;
const emitAddLine = (lines, lineType) => emit('add-line', { lines, lineType });
const emitRemoveLine = (lines, line) => emit('remove-line', { lines, line });
const rowLines = row => row.section?.lines || model.value.unsectioned_lines;
const displayedLine = line => ({
    get item() { return line.item_nm; }, set item(value) { line.item_nm = value; },
    get part() { return line.part_nm; }, set part(value) { line.part_nm = value; },
    get specification() { return line.specification; }, set specification(value) { line.specification = value; },
    get unit() { return line.unit_nm; }, set unit(value) { line.unit_nm = value; },
    get notes() { return line.notes; }, set notes(value) { line.notes = value; }
  });
const displayedSection = section => ({
      get name() { return section.section_nm; }, set name(value) { section.section_nm = value; },
      get notes() { return section.notes; }, set notes(value) { section.notes = value; },
      get unit() { return section.unit_nm; }, set unit(value) { section.unit_nm = value; }
    });
const captureQuantityDecimalPlaces = (record, rawValue) => {
  const decimalPart = String(rawValue ?? '').split('.')[1];
  record.quantity_decimal_places = rawValue === '' ? null : Math.min(decimalPart?.length || 0, 2);
};
const formatNumber = value => Math.round(Number(value || 0)).toLocaleString(isEnglish.value ? 'en-US' : 'ja-JP');
const roman = value => {
  const unicodeRoman = ['Ⅰ', 'Ⅱ', 'Ⅲ', 'Ⅳ', 'Ⅴ', 'Ⅵ', 'Ⅶ', 'Ⅷ', 'Ⅸ', 'Ⅹ', 'Ⅺ', 'Ⅻ'];
  if (unicodeRoman[value - 1]) return unicodeRoman[value - 1];
  const values = [[10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
  let number = Number(value || 0);
  return values.reduce((result, [amount, character]) => {
    while (number >= amount) { result += character; number -= amount; }
    return result;
  }, '');
};
</script>

<style scoped>
.estimate-document-editor {
  --document-border: #30363b;
  --input-border: #ced4da;
  --input-bg: #fff;
  color: #17202a;
}

.editor-toolbar {
  position: sticky;
  top: 0;
  z-index: 25;
  display: flex;
  margin-bottom: 0;
  padding: 10px 16px;
  border: 1px solid #2c3947;
  border-radius: 4px 4px 0 0;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #fff;
  background: #2f3c4a;
  box-shadow: 0 2px 7px rgba(30, 52, 68, 0.16);
}

.editor-title {
  display: flex;
  align-items: baseline;
  gap: 7px;
  font-size: 0.92rem;
}

.editor-title strong {
  font-weight: 700;
}

.editor-title span {
  color: #d8e2ec;
  font-size: 0.82rem;
}

.toolbar-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.editor-options {
  display: flex;
  padding: 8px 12px;
  border-right: 1px solid #c7d2dc;
  border-bottom: 1px solid #c7d2dc;
  border-left: 1px solid #c7d2dc;
  align-items: center;
  background: #f8fafc;
}

.toolbar-group--settings { gap: 12px; }
.toolbar-control { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.toolbar-control + .toolbar-control { padding-left: 12px; border-left: 1px solid #c7d2dc; }
.toolbar-group--right { justify-content: flex-end; }
.toolbar-label { margin-right: 4px; font-size: 0.8rem; font-weight: 700; }

.management-toggle-bar {
  display: flex;
  width: 100%;
  margin: 14px 0 0;
  padding: 10px 14px;
  border: 1px solid #cbd5df;
  border-radius: 3px;
  align-items: center;
  gap: 8px;
  color: #4e6070;
  background: #f3f6fa;
  font-size: 0.78rem;
  text-align: left;
}

.management-toggle-bar:hover {
  border-color: #aebdcb;
  background: #edf2f7;
}

.management-toggle-caret {
  color: #365c84;
  font-size: 0.7rem;
}

.management-toggle-title {
  color: #314354;
  font-weight: 700;
}

.management-toggle-summary {
  color: #657787;
}

.management-toggle-help {
  margin-left: auto;
  color: #8a99a7;
  font-size: 0.7rem;
}

.management-panel {
  margin: 0 0 16px;
  padding: 20px;
  border: 1px solid #b8c7d3;
  border-top: 0;
  border-radius: 0 0 4px 4px;
  background: #f4f8fb;
}

.management-heading { display: flex; margin-bottom: 16px; align-items: flex-end; justify-content: space-between; gap: 20px; }
.management-heading h2 { margin: 0; font-size: 1.2rem; }
.management-heading p { margin: 0; color: #607483; font-size: 0.8rem; }
.management-kicker { color: #668094; font-size: 0.65rem; font-weight: 800; letter-spacing: 0.1em; }
.section-management summary { cursor: pointer; font-weight: 700; }

.document-stage {
  margin-top: 16px;
  padding: 16px;
  overflow-x: auto;
  border: 1px solid #d4dbe2;
  border-radius: 3px;
  background: #f5f6f7;
}

.document-paper {
  position: relative;
  box-sizing: border-box;
  margin: 0 auto 22px;
  padding: 28px;
  border: 1px solid #aeb8c4;
  background: #fff;
  box-shadow: 0 1px 4px rgba(21, 39, 52, 0.08);
  font-family: "Yu Mincho", "Hiragino Mincho ProN", serif;
}

.document-paper--portrait {
  width: 1280px;
  max-width: 100%;
  min-height: 0;
  padding: 36px 28px 28px;
}
.document-paper--landscape { width: 1380px; min-height: 880px; }

.continuation-header,
.landscape-page-header {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  min-height: 90px;
  align-items: start;
}

.continuation-header h2,
.landscape-page-header h2 { margin: 0; font-size: 25px; font-weight: 500; letter-spacing: 0.2em; text-align: center; }
.continuation-header h2 small { font-size: 11px; letter-spacing: 0; }
.continuation-header > :last-child,
.landscape-page-header > :last-child { text-align: right; }

.document-grid { width: 100%; table-layout: fixed; border-collapse: collapse; font-family: inherit; font-size: 12px; }
.document-grid th,
.document-grid td { height: 38px; padding: 3px 4px; border: 1px solid var(--document-border); vertical-align: middle; }
.document-grid thead th { background: #f3f1ea; font-weight: 500; text-align: center; }
.document-paper--portrait .document-grid th,
.document-paper--portrait .document-grid td { border-color: #aeb8c4; }
.document-paper--portrait .document-grid thead th { background: #f1f2f3; }
.portrait-grid th:nth-child(1) { width: 32%; }
.portrait-grid th:nth-child(2) { width: 8%; }
.portrait-grid th:nth-child(3) { width: 8%; }
.portrait-grid th:nth-child(4) { width: 12%; }
.portrait-grid th:nth-child(5) { width: 14%; }
.portrait-grid th:nth-child(6) { width: 26%; }

.document-row-add-actions {
  display: flex;
  margin-top: 6px;
  justify-content: flex-start;
  gap: 8px;
  font-family: system-ui, sans-serif;
}
.document-row-add-actions button {
  padding: 5px 10px;
  border: 1px dashed #9cb7d7;
  border-radius: 3px;
  color: #31577f;
  background: #fff;
  font-size: 11px;
}
.document-row-add-actions .frequent-work-button {
  border-style: solid;
  border-color: #9cb7d7;
  color: #234e69;
  background: #edf4fc;
}

.portrait-line-actions-row td {
  height: auto;
  padding: 0;
  border: 0;
}

/* 検索セレクトのクリアボタン（z-index: 1100）が固定ツールバーの上に出ないようにする */
.estimate-document-editor :deep(.clear-button) { z-index: 2; }
.cell-input { width: 100%; min-height: 28px; padding: 3px 5px; border: 1px solid var(--input-border); border-radius: 2px; outline: none; background: var(--input-bg); font: inherit; }
.cell-input:focus { border-color: #86b7fe; box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.18); }
/* 縦型の入力欄は請求書作成画面などと同じ標準フォームの見た目に揃える */
.document-paper--portrait .document-grid { font-size: 14px; }
.document-paper--portrait .document-grid th,
.document-paper--portrait .document-grid td { height: 46px; padding: 4px 6px; }
.document-paper--portrait .cell-input {
  min-height: 38px;
  padding: 6px 12px;
  border-color: #ced4da;
  border-radius: 0.375rem;
  background: #fff;
  font-family: var(--bs-body-font-family);
  font-size: 14px;
}
.document-paper--portrait .portrait-remarks-cell { vertical-align: top; }
.document-paper--portrait .language-fallback-input { font-size: 12px; }
.document-paper--portrait .document-row-add-actions button { padding: 6px 14px; font-size: 13px; }
.document-paper--portrait .editor-general-notes { font-size: 13px; }
.remove-record-button {
  flex: 0 0 auto;
  padding: 3px 6px;
  border: 1px solid #b75b5b;
  border-radius: 3px;
  color: #8e2f2f;
  background: #fff5f5;
  font: 10px system-ui, sans-serif;
  white-space: nowrap;
}
.remove-record-button:hover { color: #fff; background: #a94343; }
.row-action-anchor { position: relative; }
.remove-record-button--outside {
  position: absolute;
  top: 50%;
  right: -27px;
  z-index: 3;
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  line-height: 18px;
  text-align: center;
}
.language-fallback-input { margin-top: 2px; border-color: var(--input-border); background: var(--input-bg); font-family: system-ui, sans-serif; font-size: 10px; }
.number-input { text-align: right; }
.discount-amount-input { display: flex; align-items: center; gap: 4px; }
.discount-amount-input .cell-input { flex: 1; min-width: 0; }
.number-cell { text-align: right; font-variant-numeric: tabular-nums; }
.manual-amount { color: inherit; background: var(--input-bg); }
.section-cell-inputs { display: grid; grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); gap: 4px; }
.detail-remarks-editor,
.detail-remarks-display { display: flex; min-height: 28px; align-items: center; gap: 6px; }
.detail-remarks-label { flex: 0 0 46px; color: #5d6d7a; font-weight: 500; text-align: center; }
.detail-remarks-editor > .cell-input { flex: 1; }
.detail-remarks-display > :last-child { flex: 1; white-space: pre-line; }
.detail-remarks-row td { background: #fafbfc; }
.detail-remarks-row .cell-input { min-height: 24px; }
.section-row td { background: #f6f4ed; font-weight: 700; }
.note-row td { background: #fffdf5; }
.subtotal-row td,
.total-row td { font-weight: 700; text-align: right; }
.total-row td { border-top-width: 2px; }
.blank-row td { height: 30px; }
.fixed-note-row td { font-size: 11px; }

.editor-general-notes {
  margin-top: 8px;
  border: 1px solid #c5cdd5;
  background: #fafbfc;
  font-size: 11px;
}

.editor-general-notes summary {
  padding: 7px 10px;
  color: #596a78;
  cursor: pointer;
  font-weight: 500;
}

.editor-general-notes-body {
  padding: 0 8px 8px;
}

.editor-general-notes-input {
  min-height: 48px;
  resize: vertical;
}

.landscape-detail-grid { font-size: 11px; }
.landscape-detail-grid th:nth-child(1) { width: 20%; }
.landscape-detail-grid th:nth-child(2) { width: 14%; }
.landscape-detail-grid th:nth-child(3) { width: 22%; }
.landscape-detail-grid th:nth-child(4) { width: 7%; }
.landscape-detail-grid th:nth-child(5) { width: 7%; }
.landscape-detail-grid th:nth-child(6) { width: 13%; }
.landscape-detail-grid th:nth-child(7) { width: 17%; }

.landscape-detail-editor-page {
  padding: 34px;
}

.landscape-section-block + .landscape-section-block {
  margin-top: 28px;
}

.landscape-section-heading {
  display: grid;
  margin-bottom: 10px;
  grid-template-columns: auto minmax(260px, 1fr) minmax(250px, 0.7fr) auto;
  align-items: center;
  gap: 10px;
  font-family: system-ui, sans-serif;
}

.landscape-section-number {
  font-family: "Yu Mincho", "Hiragino Mincho ProN", serif;
  font-size: 18px;
  font-weight: 700;
}

.landscape-section-name .cell-input {
  font-family: "Yu Mincho", "Hiragino Mincho ProN", serif;
  font-size: 15px;
  font-weight: 700;
}

.required-field-label {
  display: flex;
  margin-bottom: 3px;
  align-items: center;
  gap: 3px;
  color: #596875;
  font: 10px system-ui, sans-serif;
}

.landscape-section-subtotal {
  color: #5a6675;
  font-size: 12px;
  white-space: nowrap;
}

.landscape-section-subtotal strong {
  margin-left: 4px;
  color: #17202a;
  font-size: 15px;
}

.landscape-section-heading-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.landscape-section-summary-settings {
  display: flex;
  margin: -3px 0 8px 34px;
  align-items: center;
  gap: 10px;
  color: #596875;
  font: 11px system-ui, sans-serif;
}

.landscape-section-summary-settings label {
  display: flex;
  align-items: center;
  gap: 5px;
}

.landscape-section-summary-settings input {
  width: 90px;
  min-height: 28px;
}

.landscape-section-notes {
  display: grid;
  margin: -3px 0 8px;
  grid-template-columns: 92px minmax(0, 1fr);
  align-items: center;
  gap: 6px;
  color: #4f5e69;
  font-family: system-ui, sans-serif;
  font-size: 10px;
}

.landscape-section-notes > span {
  text-align: center;
}

.landscape-section-heading--adjustment {
  grid-template-columns: auto minmax(0, 1fr);
}

.landscape-section-actions,
.landscape-detail-footer-actions {
  display: flex;
  margin-top: 7px;
  justify-content: flex-end;
  gap: 5px;
  font-family: system-ui, sans-serif;
}

.landscape-section-actions button,
.landscape-detail-footer-actions button {
  padding: 4px 9px;
  border: 1px solid #9b7a25;
  border-radius: 3px;
  color: #644b0b;
  background: #fffbea;
  font-size: 10px;
}

.landscape-section-actions .frequent-work-button {
  border-color: #4f7894;
  color: #234e69;
  background: #eff7fb;
}

.landscape-detail-footer-actions {
  margin-top: 22px;
}

.cover-editor-page { padding-top: 58px; }
.cover-editor-note,
.readonly-sync-note { margin-top: 18px; color: #4f6573; font-family: system-ui, sans-serif; font-size: 12px; text-align: center; }
.readonly-sync-note--top { margin: 0 0 18px; }
.editor-page-number { position: absolute; right: 30px; bottom: 15px; left: 30px; color: #5b6973; font: 11px system-ui, sans-serif; text-align: center; }
.editor-quotation-conditions { margin-top: 8px; font-size: 11px; line-height: 1.55; }

.editor-footer-actions {
  position: sticky;
  bottom: 0;
  z-index: 20;
  display: flex;
  margin-top: 14px;
  padding: 12px 14px;
  border: 1px solid #c4d0d8;
  border-radius: 8px;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 -3px 12px rgba(30, 52, 68, 0.08);
}
.add-actions,
.amount-summary { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.amount-summary { justify-content: flex-end; font-size: 0.78rem; }
.amount-summary strong { color: #123f5b; font-size: 0.92rem; }

@media (max-width: 900px) {
  .editor-toolbar,
  .editor-footer-actions,
  .management-heading { align-items: stretch; flex-direction: column; }
  .toolbar-group--right,
  .amount-summary { justify-content: flex-start; }

  .editor-title {
    flex-wrap: wrap;
  }

  .management-toggle-bar {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .management-toggle-help {
    width: 100%;
    margin-left: 18px;
  }

  .toolbar-control + .toolbar-control {
    padding-left: 0;
    border-left: 0;
  }

  .document-stage {
    padding: 10px;
  }
}
</style>
